export const REALTIME_CONTRACT = "xsou.realtime.v1";
export const REALTIME_PATH = "/dev-api/api/mts-api/realtime/v1";

/** 浏览器和Agent共用一个协议；一个实例可订阅多个业务域及资源。 */
export class RealtimeClient {
  constructor({ token, subscriptions = [], onEvent = () => {}, onReady = () => {}, onState = () => {}, onError = () => {}, onResourceError = () => {}, WebSocketClass = globalThis.WebSocket, locationLike = globalThis.location, endpointPath = REALTIME_PATH, delay = (callback, ms) => globalThis.setTimeout(callback, ms), cancel = timer => globalThis.clearTimeout(timer) }) {
    // Window原生定时器需要正确接收者，不能作为client.delay/client.cancel裸调用。
    Object.assign(this, { token, onEvent, onReady, onState, onError, onResourceError, WebSocketClass, locationLike, endpointPath, delay, cancel });
    this.subscriptions = new Map();
    subscriptions.forEach(({ channel, resourceId }) => this.subscribe(channel, resourceId));
    this.socket = null; this.authenticated = false; this.closed = false; this.retry = 0; this.timers = new Set(); this.epoch = 0;
  }
  later(callback, ms) {
    const timer = this.delay(() => { this.timers.delete(timer); callback(); }, ms);
    this.timers.add(timer); return timer;
  }
  send(type, fields = {}) {
    if (this.socket?.readyState === 1) this.socket.send(JSON.stringify({ schemaVersion: 1, contractVersion: REALTIME_CONTRACT, type, ...fields }));
  }
  subscribe(channel, resourceId) {
    if (!/^[a-z][a-z0-9-]{0,63}$/.test(channel) || !/^[A-Za-z0-9_-]{1,128}$/.test(resourceId)) throw new Error("Invalid realtime subscription");
    const resource = { channel, resourceId };
    this.subscriptions.set(`${channel}:${resourceId}`, resource);
    if (this.authenticated) this.send("SUBSCRIBE", resource);
  }
  unsubscribe(channel, resourceId) {
    if (!this.subscriptions.delete(`${channel}:${resourceId}`)) return;
    if (this.authenticated) this.send("UNSUBSCRIBE", { channel, resourceId });
  }
  connect() {
    if (this.closed || this.socket || !this.token) return;
    this.onState("CONNECTING");
    const protocol = this.locationLike.protocol === "https:" ? "wss:" : "ws:";
    let socket;
    try { socket = new this.WebSocketClass(`${protocol}//${this.locationLike.host}${this.endpointPath}`); }
    catch (error) { this.onError(error); this.reconnect(); return; }
    this.epoch += 1;
    this.socket = socket; this.authenticated = false; this.lastReceived = Date.now();
    socket.onopen = () => { if (socket === this.socket) this.send("AUTH", { token: this.token }); };
    socket.onmessage = ({ data }) => {
      if (this.closed || this.socket !== socket) return;
      try {
        const frame = JSON.parse(data);
        if (frame.contractVersion !== REALTIME_CONTRACT || frame.schemaVersion !== 1) throw new Error("Unsupported realtime contract");
        this.lastReceived = Date.now();
        if (frame.type === "AUTHENTICATED") {
          if (this.authenticated) return;
          this.authenticated = true; this.retry = 0;
          this.subscriptions.forEach((resource) => this.send("SUBSCRIBE", resource));
          this.heartbeat(socket, Math.max(5, Math.min(Number(frame.heartbeatSeconds) || 30, 60)) * 1000);
          if (!this.subscriptions.size) this.onState("CONNECTED");
        } else if (frame.type === "SUBSCRIBED") {
          if (!this.subscriptions.has(`${frame.channel}:${frame.resourceId}`)) return;
          this.onState("CONNECTED"); this.deliver(this.onReady, frame);
        } else if (frame.type === "EVENT") {
          if (!this.authenticated || !this.subscriptions.has(`${frame.channel}:${frame.resourceId}`) && !this.subscriptions.has(`${frame.channel}:self`)) return;
          this.deliver(this.onEvent, frame);
        } else if (frame.type === "ERROR") {
          const error = Object.assign(new Error("Realtime request rejected"), { code: frame.code, scope: frame.scope, channel: frame.channel, resourceId: frame.resourceId });
          if (frame.scope === "RESOURCE") {
            if (!this.subscriptions.delete(`${frame.channel}:${frame.resourceId}`)) return;
            this.onResourceError(error); return;
          }
          if (frame.retryable === false) { this.onError(error); this.close(); this.onState("REJECTED"); return; }
          throw error;
        }
      } catch (error) { this.onError(error); socket.close(); }
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.epoch += 1; this.socket = null; this.authenticated = false;
      if (!this.closed) this.reconnect();
    };
    socket.onerror = () => { if (this.socket === socket) socket.close(); };
    this.later(() => { if (this.socket === socket && !this.authenticated) socket.close(); }, 10000);
  }
  deliver(callback, frame) {
    const epoch = this.epoch;
    Promise.resolve().then(() => { if (!this.closed && epoch === this.epoch && (this.subscriptions.has(`${frame.channel}:${frame.resourceId}`) || this.subscriptions.has(`${frame.channel}:self`))) return callback(frame); }).catch((error) => { if (!this.closed) this.onError(error); });
  }
  heartbeat(socket, ms) {
    this.later(() => {
      if (this.closed || this.socket !== socket) return;
      if (Date.now() - this.lastReceived > Math.max(75000, ms * 3)) { socket.close(); return; }
      this.send("PING"); this.heartbeat(socket, ms);
    }, ms);
  }
  reconnect() {
    if (this.closed) return;
    this.onState("DISCONNECTED");
    this.later(() => this.connect(), Math.min(1000 * 2 ** this.retry++, 30000));
  }
  close() {
    this.epoch += 1; this.closed = true; this.timers.forEach(this.cancel); this.timers.clear();
    const socket = this.socket; this.socket = null; this.authenticated = false;
    socket?.close(); this.token = ""; this.subscriptions.clear(); this.onState("CLOSED");
  }
}
